class DynamicVideoComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializePlayFunctionality();
  }

  initializePlayFunctionality() {
    const playButton = this.querySelector('#play_button');
    const coverImage = this.querySelector('#ovrly_img');
    const video = this.querySelector('iframe');

    const playVideo = () => {
      if (video) {
        this.playIframeVideo(video);
      }
      // Hide play button and cover image on play
      if (playButton) {
        playButton.style.display = 'none';
      }
      if (coverImage) {
        coverImage.style.display = 'none';
      }
    };

    // Add event listeners to play button and cover image to play video on click
    if (playButton) {
      playButton.addEventListener('click', playVideo);
    }
    if (coverImage) {
      coverImage.addEventListener('click', playVideo);
    }
  }

  playIframeVideo(video) {
    // Check the video URL to determine if it's YouTube or Vimeo
    const videoSrc = video.src;
    if (videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be')) {
      // YouTube video play command
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (videoSrc.includes('vimeo.com')) {
      // Vimeo video play command
      video.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
    }
  }
}
if (!window.customElements.get("dynamic-video")) {
  window.customElements.define('dynamic-video', DynamicVideoComponent);
}

class PaymentInstalments extends HTMLElement {
  constructor() {
    super(); 
    this.modalContainer = this.querySelector('.instalments-modal-container');
    this.modal = this.modalContainer.querySelector('.instalments-modal');
    this.modalOverlay = this.modalContainer.querySelector('.instalments-modal__overlay');
    this.modalToggles = this.querySelectorAll('.instalments__modal-trigger');

    // Store the original parent and next sibling for restoring position
    this.originalParent = this.modalContainer.parentNode;
    this.originalNextSibling = this.modalContainer.nextSibling;

    this.attachEventListeners();
  }

  attachEventListeners() {
    this.modalToggles.forEach(toggle => {
      toggle.addEventListener('click', () => this.openModal());
    });

    document.addEventListener('variant:change', (event) => this.handleVariantChange(event));
    document.addEventListener('cartcount:update', (event) => this.handleCartChange(event));
  }

  openModal() {
    // prevent any window refresh
    event.preventDefault();

    // Save the element that was focused before opening the modal
    this.focusedElementBeforeModal = document.activeElement;

    // add the lock class to the html element to prevent scrolling
    document.documentElement.classList.add('lock-scrolling');

    // Move the modal container to the end of the body element
    document.body.appendChild(this.modalContainer);

    // Event listeners for modal functionality
    this.modal.addEventListener('keydown', (e) => this.trapTabKey(e));
    this.modalOverlay.addEventListener('click', () => this.closeModal());

    // Close buttons inside modal
    const closeButtons = this.modal.querySelectorAll('.instalments-modal__close');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeModal());
    });

    // Show the modal and overlay
    this.modal.style.display = 'block';
    this.modalOverlay.style.display = 'block';
    this.modal.classList.remove('modal-closed');

    // Set focus to the first focusable element in the modal
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  closeModal() {
    // prevent any window refresh
    event.preventDefault();

    // remove the lock class to the html element to prevent scrolling
    document.documentElement.classList.remove('lock-scrolling');

    // Return the modal container to its original position
    if (this.originalNextSibling) {
      this.originalParent.insertBefore(this.modalContainer, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(this.modalContainer);
    }

    this.modal.classList.add('modal-closed');
    this.modal.style.display = 'none';
    this.modalOverlay.style.display = 'none';

    // Return focus to the element that had it before the modal was opened
    if (this.focusedElementBeforeModal) {
      this.focusedElementBeforeModal.focus();
    }
  }

  trapTabKey(e) {
    const focusableElements = this.getFocusableElements();
    const firstTabStop = focusableElements[0];
    const lastTabStop = focusableElements[focusableElements.length - 1];

    if (e.keyCode === 9) { 
      if (e.shiftKey) { 
        if (document.activeElement === firstTabStop) {
          e.preventDefault();
          lastTabStop.focus();
        }
      } else {
        if (document.activeElement === lastTabStop) {
          e.preventDefault();
          firstTabStop.focus();
        }
      }
    }

    if (e.keyCode === 27) {
      this.closeModal();
    }
  }

  handleCartChange(event) {
    const cartTotal = event.detail.response.total_price;
    const instalmentElements = this.querySelectorAll('#instalmentAmountFormatted');
    const money_format = document.documentElement.getAttribute('data-money-format');

    instalmentElements.forEach(element => {
      const instalmentsCount = this.getAttribute('data-instalments-count');
      if (instalmentsCount === 0) {
        return;
      }
      const instalmentAmount = cartTotal / instalmentsCount;

      // Update the displayed instalment amount
      element.textContent = Shopify.formatMoney(
        instalmentAmount,
        money_format
      );
    });
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    const instalmentElements = this.querySelectorAll('#instalmentAmountFormatted');
    const money_format = document.documentElement.getAttribute('data-money-format');

    instalmentElements.forEach(element => {
      const instalmentsCount = this.getAttribute('data-instalments-count');
      if (instalmentsCount === 0) {
        return;
      }
      const instalmentAmount = variant.price / instalmentsCount;

      // Update the displayed instalment amount
      element.textContent = Shopify.formatMoney(
        instalmentAmount,
        money_format
      );
    });
  }

  getFocusableElements() {
    const focusableElementsString = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
    return Array.from(this.modal.querySelectorAll(focusableElementsString));
  }
}

if (!window.customElements.get('payment-instalments')) {
  window.customElements.define('payment-instalments', PaymentInstalments);
}

class ParallaxBackground extends HTMLElement {
  constructor() {
    super();
    this.lastKnownScrollPosition = 0;
    this.ticking = false;
  }

  connectedCallback() {
    this.initParallax();
  }

  initParallax() {
    const parallaxBackground = this.querySelector('.parallax__background');
    const mobileThreshold = 0.43;
    const tabletThreshold = 0.57;
    const desktopThreshold = 0.65;
    const downAnimationOnly = false;
    let sectionHeight = this.offsetHeight;
    let isMobile = window.innerWidth <= 768;
    let isTablet = window.innerWidth <= 1200;
    let threshold = isMobile ? mobileThreshold : isTablet ? tabletThreshold : desktopThreshold;

    const updateThreshold = () => {
      isMobile = window.innerWidth <= 768;
      isTablet = window.innerWidth <= 1200;
      threshold = isMobile ? mobileThreshold : isTablet ? tabletThreshold : desktopThreshold;
      sectionHeight = this.offsetHeight;
    };

    const updateBackgroundHeight = (scrollPos) => {
      const windowHeight = window.innerHeight;
      const sectionTop = this.offsetTop;
      const startAnimationAt = sectionTop - windowHeight * threshold;
      const endAnimationAt = sectionTop + sectionHeight - windowHeight * threshold;

      let newHeight = 0;
      if (scrollPos > startAnimationAt && scrollPos < endAnimationAt) {
        let progress = (scrollPos - startAnimationAt) / (endAnimationAt - startAnimationAt);
        newHeight = progress * sectionHeight;
      } else if (scrollPos >= endAnimationAt) {
        newHeight = sectionHeight;
      }
      let current = parseInt(parallaxBackground.style.height, 10);
      if (isNaN(current) || newHeight > current || !downAnimationOnly) {
        parallaxBackground.style.height = `${newHeight}px`;
      }
    };

    window.addEventListener('resize', () => {
      updateThreshold();
    });

    window.addEventListener('scroll', () => {
      this.lastKnownScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updateBackgroundHeight(this.lastKnownScrollPosition);
          this.ticking = false;
        });

        this.ticking = true;
      }
    });

    window.addEventListener('load', () => {
      this.lastKnownScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updateBackgroundHeight(this.lastKnownScrollPosition);
          this.ticking = false;
        });

        this.ticking = true;
      }
    });
  }
}
if (!window.customElements.get("parallax-background")) {
  customElements.define('parallax-background', ParallaxBackground);
}

const invertedMediaQuery = window.matchMedia("(inverted-colors: inverted)");

function handleColorInversionChange(event) {
  const default_logo = document.querySelector('.site-logo-image');
  const inverted_logo = document.querySelector('.site-logo-image.site-logo-image--inverted');

  if (!default_Logo || !inverted_Logo) return;

  if (event.matches) {
    // hide the default logo and show the inverted logo 
    default_logo.style.display = 'none';
    inverted_logo.style.display = 'block';
  } else {
    // hide the inverted logo and show the default logo 
    default_logo.style.display = 'block';
    inverted_logo.style.display = 'none';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  handleColorInversionChange(invertedMediaQuery);          
  invertedMediaQuery.addEventListener('change', handleColorInversionChange);
});

// Shopify Embedded Form CSS Solution

if (document.body.classList.contains('page-corporate-bulk-ordering')) {
  const intervalId = setInterval(() => {
    const form = document.querySelector('#app-embed');

    if (form && form.shadowRoot) {
      clearInterval(intervalId);

      const shadow = form.shadowRoot;

      const sheet = new CSSStyleSheet();
      sheet.insertRule(`
        h2._textHeading_2aowh_35 {
          font-family: rakuten-sans, sans-serif;
          font-size: 30px;
          margin-block-end: 20px;
        }
      `, 0);
      sheet.insertRule(`
        @media (max-width: 768px) {
          h2._textHeading_2aowh_35 {
            font-size: 24px;
          }
        }
      `, 1);
      sheet.insertRule(`
        p {
          font-family: rakuten-sans, sans-serif;
          font-size: 16px;
          line-height: 1.5625rem;
        }
      `, 2);
      sheet.insertRule(`
        input, label, button {
          font-family: rakuten-sans, sans-serif !important;
        }
      `, 3);

      shadow.adoptedStyleSheets.push(sheet);
    }
  }, 200); 
}

export {
  DynamicVideoComponent,
  ParallaxBackground,
  PaymentInstalments
}